/**
 * The offline bar at the top of the crew app and My shifts: "No signal" with when the job packs were saved, and
 * "Waiting to send" with each saved action, sending them as soon as the phone is back online.
 */
import { useCallback, useEffect, useState } from "react";
import { Icon } from "../../components/Icon";
import { crewFetch, crewBlob, sendQueued } from "../../lib/api";
import { fmtTime } from "../../lib/format";
import { PREVIEW } from "../../lib/preview";
import { SectionHelp } from "../help/Help";
import { offlineState, OFFLINE_EVENT, saveAhead } from "./cache";
import { flush, OUTBOX_EVENT, pending, pruneResults, remove, type QueuedAction } from "./outbox";

/** Send what is waiting, now. */
export const sendWaiting = () => flush(sendQueued).then(async (r) => (r.left === 0 ? (await pruneResults(), r) : r));

/** Save the job packs for the next 48 hours (after My shifts loads). */
export const saveUpcoming = (shifts: { id: string; startsAt: string; endsAt: string }[]) =>
  PREVIEW ? Promise.resolve(0) : saveAhead(shifts, (p) => crewFetch(p), (p) => crewBlob(p, { timeoutMs: 120_000 }));

/** Re-render when the saved actions or the online state change. */
export function useOutbox() {
  const [items, setItems] = useState<QueuedAction[]>([]);
  const [offlineSince, setOfflineSince] = useState<string | null>(offlineState());
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const refresh = useCallback(() => {
    pending().then(setItems, () => setItems([]));
    setOfflineSince(offlineState());
  }, []);
  useEffect(() => {
    refresh();
    const up = () => {
      setOnline(true);
      void sendWaiting();
    };
    const down = () => setOnline(false);
    window.addEventListener(OUTBOX_EVENT, refresh);
    window.addEventListener(OFFLINE_EVENT, refresh);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    // Try now, and every 30 seconds while anything is waiting.
    void sendWaiting();
    const t = window.setInterval(() => {
      if (navigator.onLine) void pending().then((p) => (p.some((a) => a.state === "waiting") ? sendWaiting() : undefined));
    }, 30_000);
    return () => {
      window.removeEventListener(OUTBOX_EVENT, refresh);
      window.removeEventListener(OFFLINE_EVENT, refresh);
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      window.clearInterval(t);
    };
  }, [refresh]);
  return { items, offlineSince, online };
}

export function OfflineBar({ bookingId }: { bookingId?: string }) {
  const { items, offlineSince, online } = useOutbox();
  const [busy, setBusy] = useState(false);
  const mine = bookingId ? items.filter((a) => a.bookingId === bookingId || a.bookingId === null) : items;
  const waiting = mine.filter((a) => a.state === "waiting");
  const failed = mine.filter((a) => a.state === "failed");
  if (online && !offlineSince && !mine.length) return null;
  return (
    <div className="stack" style={{ gap: 8 }}>
      {(!online || offlineSince) && (
        <p className="notice notice--error" role="status">
          <Icon name="warning" size={18} />
          {offlineSince ? `No signal. Showing the job pack saved at ${fmtTime(offlineSince)}.` : "No signal. You can keep working: what you do is saved on this phone."}
          <SectionHelp pageId="my-shifts">Working offline</SectionHelp>
        </p>
      )}
      {waiting.length > 0 && (
        <div className="notice notice--ok" role="status">
          <Icon name="refresh" size={18} />
          <div className="stack" style={{ gap: 4 }}>
            <p className="strong" style={{ margin: 0 }}>Waiting to send: {waiting.length}</p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {waiting.map((a) => <li key={a.id}>{a.label}, saved {fmtTime(a.queuedAt)}</li>)}
            </ul>
            {online && (
              <button type="button" className="btn btn--outline btn--xs" disabled={busy} aria-busy={busy} onClick={() => { setBusy(true); void sendWaiting().finally(() => setBusy(false)); }}>
                Send now
              </button>
            )}
          </div>
        </div>
      )}
      {failed.map((a) => (
        <div key={a.id} className="notice notice--error" role="alert">
          <Icon name="warning" size={18} />
          <div className="stack" style={{ gap: 4 }}>
            <p style={{ margin: 0 }}><strong>Couldn't send: {a.label}</strong> (saved {fmtTime(a.queuedAt)}). {a.error}</p>
            <button type="button" className="btn btn--outline btn--xs" onClick={() => void remove(a.id)}>Remove it</button>
          </div>
        </div>
      ))}
    </div>
  );
}
